import type { RouteLocationNormalizedGeneric } from 'vue-router';
import { stepForRoute } from '~/utils/acquisition';

export default defineNuxtRouteMiddleware(
   async (
      to: RouteLocationNormalizedGeneric,
      from: RouteLocationNormalizedGeneric
   ) => {
      const { page } = useSegment();
      const { store: clientStore } = useAuthClientStorage();
      const { store: therapistStore } = useAuthTherapistStorage();
      // Query strings can contain auth credentials and signup email addresses.
      page(to.path);
      const acquisition = useAcquisition();
      acquisition.captureVisit(new URL(to.fullPath, window.location.origin), document.referrer);
      const capturedHandoff = useState<string | null>('redemption-entry-token', () => null);
      const handoffToken = typeof to.query.redemption_token === 'string'
         ? to.query.redemption_token : capturedHandoff.value || '';
      const routeName = String(to.name || '');
      // Register owns its next-question routing; other client entries consume before auth guards redirect.
      if (handoffToken && !routeName.startsWith('auth-client-register-') &&
         (routeName.startsWith('auth-client-') || routeName.startsWith('client-'))) {
         capturedHandoff.value = null;
         const auth = useClientAuthStore();
         const installed = await auth.consumeRedemptionHandoff(handoffToken);
         const authenticated = useAuthClientStorage().isClientAuthenticated.value;
         if (installed && authenticated) {
            await auth.attachPendingRedemption();
         }
         const safeQuery = { ...to.query };
         delete safeQuery.redemption_token;
         delete safeQuery.tt_attribution;
         const target = installed && authenticated && routeName === 'auth-client-login'
            ? { name: 'client-booking' } : { path: to.path };
         return navigateTo({ ...target, query: safeQuery }, { replace: true });
      }
      if (
         to?.query?.access_token &&
         to?.query?.refresh_token &&
         to?.query?.role
      ) {
         const { access_token, refresh_token, role } = to.query;
         if (role == 'client') {
            clientStore({
               access_token: access_token as string,
               refresh_token: refresh_token as string,
            });
            if (to.path) return navigateTo(to.path);
            else return navigateTo('/client/booking');
         } else if (role == 'therapist') {
            therapistStore({
               access_token: access_token as string,
               refresh_token: refresh_token as string,
            });
            if (to.path) return navigateTo(to.path);
            else return navigateTo('/therapist/calendar');
         }
      }
      if (String(to.name || '').startsWith('client-')) {
         await useClientAuthStore().attachPendingRedemption();
         await acquisition.ensureClientAcquisition();
         const stage = stepForRoute(to.name);
         if (stage) await acquisition.trackClientStep(stage);
      }
   }
);
